import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColumnHeaderParams,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import { GridColDef } from '@mui/x-data-grid-premium';
import { GridEventListener } from '@mui/x-data-grid/models/events';
import uniqueId from 'lodash/uniqueId';

import { STORAGE_KEYS } from '@sbv-clients/util-misc';

import { Icon } from '../icon';

// ─── Persistence ──────────────────────────────────────────────────────────────

type GridItemState = {
  columnWidthMap: Record<string, number>;
  columnVisibilityMap?: Record<string, boolean>;
  filterModel?: GridFilterModel;
};
export type GridState = Record<string, GridItemState>;

const loadGridState = (): GridState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.GRID_STATE);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveGridState = (data: GridState) => {
  try {
    localStorage.setItem(STORAGE_KEYS.GRID_STATE, JSON.stringify(data));
  } catch {
    /* empty */
  }
};

const useGridState = () => {
  const [data, setData] = useState<GridState>(() => loadGridState());

  const saveByName = useCallback(
    (gridName: string, patch: Partial<GridItemState>) => {
      setData((prev) => {
        const existing = prev[gridName] ?? { columnWidthMap: {} };
        const next = { ...prev, [gridName]: { ...existing, ...patch } };
        saveGridState(next);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.GRID_STATE) setData(loadGridState());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return { data, saveByName };
};

const useGridStateByName = (
  gridName: string,
  gridState: ReturnType<typeof useGridState>
) => {
  const { data, saveByName } = gridState;
  const saveWidths = useCallback(
    (widths: Record<string, number>) =>
      saveByName(gridName, { columnWidthMap: widths }),
    [gridName, saveByName]
  );
  const saveVisibility = useCallback(
    (visibility: Record<string, boolean>) =>
      saveByName(gridName, { columnVisibilityMap: visibility }),
    [gridName, saveByName]
  );
  const saveFilterModel = useCallback(
    (filterModel: GridFilterModel) => saveByName(gridName, { filterModel }),
    [gridName, saveByName]
  );
  return {
    gridItemState: data[gridName],
    saveWidths,
    saveVisibility,
    saveFilterModel,
  };
};

// ─── Filter model sanitization ───────────────────────────────────────────────

/**
 * Removes stored filter items whose operator is no longer valid for the column.
 * Only checks columns that declare explicit `filterOperators` — those are the
 * ones whose operator set can change (e.g. when a column type is migrated from
 * `dateTime` → `string`). MUI-default-operator columns are left untouched.
 */
const sanitizeFilterModel = (
  filterModel: GridFilterModel,
  columns: readonly GridColDef[]
): GridFilterModel => {
  const columnMap = new Map(columns.map((c) => [c.field, c]));
  const validItems = filterModel.items.filter((item) => {
    const col = columnMap.get(item.field);
    if (!col?.filterOperators?.length) return true;
    return col.filterOperators.some((op) => op.value === item.operator);
  });
  if (validItems.length === filterModel.items.length) return filterModel;
  return { ...filterModel, items: validItems };
};

// ─── Column width pipeline ────────────────────────────────────────────────────

// MUI DataGrid has internal overhead (pinned-section border separators, etc.)
// not reflected in containerWidth. Keeping columns 2px short of the container
// ensures the horizontal scrollbar never appears after distribution.
const SCROLL_GUARD_PX = 1;

type Cols = readonly GridColDef[];

/** True when every column has a declared width that collectively exceeds the container. */
const columnsExceedContainer = (columns: Cols, containerWidth: number) =>
  columns.every((c) => c.width != null) &&
  columns.reduce((sum, c) => sum + (c.width ?? 0), 0) > containerWidth;

const PINNED_COLUMN_DEFAULT_WIDTH = 150;

/**
 * Computes initial widths for first-time load.
 * Pinned columns use their declared width (fallback: 150) and are NOT stored —
 * they always render from the prop. Remaining space is split evenly across
 * non-pinned columns; remainder pixels are distributed 1-px each so the total
 * equals containerWidth exactly.
 */
const computeInitialWidths = (
  columns: Cols,
  containerWidth: number,
  pinnedFields: Set<string>,
  /** Width already reserved for content outside `columns` (e.g. the flex grouping column). */
  reservedWidth = 0
): Record<string, number> => {
  const pinned = columns.filter((c) => pinnedFields.has(c.field));
  const nonPinned = columns.filter((c) => !pinnedFields.has(c.field));

  const totalPinned = pinned.reduce(
    (sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH),
    0
  );
  const available =
    containerWidth - totalPinned - reservedWidth - SCROLL_GUARD_PX;

  // Reserve each column's minWidth first, then distribute the leftover evenly.
  const colMinWidths = nonPinned.map((c) => c.minWidth ?? 0);
  const totalMin = colMinWidths.reduce((sum, w) => sum + w, 0);
  const leftover = Math.max(available - totalMin, 0);
  const base =
    nonPinned.length > 0 ? Math.floor(leftover / nonPinned.length) : 0;
  const rem = nonPinned.length > 0 ? leftover % nonPinned.length : 0;

  // Only non-pinned widths are stored; pinned columns always read from their prop.
  const result: Record<string, number> = {};
  for (let i = 0; i < nonPinned.length; i++)
    result[nonPinned[i].field] = colMinWidths[i] + base + (i < rem ? 1 : 0);
  return result;
};

/**
 * Returns the initial pixel width to reserve for the flex grouping column so it
 * gets its proportional share of the available space (based on flex values) and
 * is persisted to storage alongside the regular columns on first load.
 */
const computeGroupingColInitialWidth = (
  groupingColDef: DataGridPremiumProps['groupingColDef'],
  visibleNonPinnedCols: Cols,
  available: number
): number => {
  const groupingFlex = (groupingColDef as GridColDef | undefined)?.flex ?? 1;
  const regularFlexTotal = visibleNonPinnedCols.reduce(
    (sum, c) => sum + (c.flex ?? 1),
    0
  );
  const totalFlex = groupingFlex + regularFlexTotal;
  return Math.max(
    Math.round((available * groupingFlex) / totalFlex),
    (groupingColDef as GridColDef | undefined)?.minWidth ?? 50
  );
};

/** Pipeline step 1 — apply min/max bounds to a column copy. */
const applyBounds =
  (applyDefaultBounds: boolean) =>
  (col: GridColDef): GridColDef =>
    applyDefaultBounds
      ? { ...col, minWidth: col.minWidth ?? 50, maxWidth: col.maxWidth }
      : { ...col };

// ─── Column visibility modal ──────────────────────────────────────────────────

const ColumnVisibilityModal = ({
  open,
  columns,
  pinnedFields,
  visibilityModel,
  onClose,
  onApply,
}: {
  open: boolean;
  columns: GridColDef[];
  pinnedFields: Set<string>;
  visibilityModel: Record<string, boolean>;
  onClose: () => void;
  onApply: (model: Record<string, boolean>) => void;
}) => {
  const [local, setLocal] = useState<Record<string, boolean>>(visibilityModel);

  // Reset local state whenever the modal opens
  useEffect(() => {
    if (open) setLocal(visibilityModel);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleableColumns = columns.filter(
    (c) =>
      !c.field.startsWith('__') &&
      c.hideable !== false &&
      !pinnedFields.has(c.field)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Customize Grid Columns</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight="600">
              GRID VIEW COLUMNS
            </Typography>
            <Typography variant="caption" color="grey.500">
              Available columns for the grid
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() =>
              setLocal(
                Object.fromEntries(
                  toggleableColumns.map((c) => [c.field, true])
                )
              )
            }
          >
            Select All
          </Button>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 300,
              overflowY: 'auto',
            }}
          >
            {toggleableColumns.map((col, index) => (
              <FormControlLabel
                key={col.field}
                sx={{
                  mx: 0,
                  px: 1,
                  borderBottom:
                    index < toggleableColumns.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={local[col.field] !== false}
                    onChange={() =>
                      setLocal((prev) => ({
                        ...prev,
                        [col.field]: !(prev[col.field] ?? true),
                      }))
                    }
                  />
                }
                label={col.headerName ?? col.field}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 6 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onApply(local)}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const GROUPING_COL_FIELD = '__row_group_by_columns_group__';

export const StateAwareGridPremium = ({
  name,
  columns = [],
  applyDefaultBounds = true,
  withAutoFit = true,
  withColumnVisibilityIcon = false,
  withFilterPersistence = false,
  getRowId: customGetRowId,
  pinnedColumns,
  initialState,
  groupingColDef,
  onFilterModelChange: callerOnFilterModelChange,
  ...props
}: DataGridPremiumProps & {
  name: string;
  applyDefaultBounds?: boolean;
  withAutoFit?: boolean;
  withColumnVisibilityIcon?: boolean;
  withFilterPersistence?: boolean;
}) => {
  const getRowId = customGetRowId ?? ((row: any) => row.id ?? uniqueId('row-'));

  const gridState = useGridState();
  const { gridItemState, saveWidths, saveVisibility, saveFilterModel } =
    useGridStateByName(name, gridState);

  const pinnedFields = useMemo(
    () =>
      new Set([
        ...(pinnedColumns?.left ?? []),
        ...(pinnedColumns?.right ?? []),
        ...(initialState?.pinnedColumns?.left ?? []),
        ...(initialState?.pinnedColumns?.right ?? []),
      ]),
    [pinnedColumns, initialState?.pinnedColumns]
  );

  // ── Column visibility ────────────────────────────────────────────────────────

  // Columns hidden via initialState (e.g. grouped columns from useKeepGroupedColumnsHidden)
  // must always take precedence — they are structural, not user-controlled.
  const structuralVisibility = useMemo(
    () => initialState?.columns?.columnVisibilityModel ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialState?.columns?.columnVisibilityModel]
  );

  // Derive hidden fields directly from rowGroupingModel so grouped columns are
  // ALWAYS invisible regardless of state timing or initialState chain.
  // This is the authoritative source — no circular dependency through apiRef.
  const rowGroupingHidden = useMemo(
    () =>
      Object.fromEntries(
        ((props.rowGroupingModel as string[] | undefined) ?? []).map((f) => [
          f,
          false,
        ])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(props.rowGroupingModel)]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(() => ({
    ...(loadGridState()[name]?.columnVisibilityMap ?? {}),
    ...structuralVisibility,
  }));

  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);

  // Re-apply structural hidden columns whenever initialState changes
  // (e.g. rowGroupingModel changes and useKeepGroupedColumnsHidden recalculates).
  useEffect(() => {
    const structural = initialState?.columns?.columnVisibilityModel;
    if (!structural) return;
    setColumnVisibilityModel((prev) => ({ ...prev, ...structural }));
  }, [initialState?.columns?.columnVisibilityModel]);

  // Sync visibility when storage changes (e.g. cross-tab).
  useEffect(() => {
    setColumnVisibilityModel({
      ...(gridItemState?.columnVisibilityMap ?? {}),
      ...(initialState?.columns?.columnVisibilityModel ?? {}),
    });
  }, [gridItemState]);

  // ── Filter model ─────────────────────────────────────────────────────────────

  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    if (!withFilterPersistence) return { items: [] };
    const stored = loadGridState()[name]?.filterModel ?? { items: [] };
    return sanitizeFilterModel(stored, columns);
  });

  // Sync filter model when storage changes (e.g. cross-tab).
  useEffect(() => {
    if (!withFilterPersistence) return;
    const stored = gridItemState?.filterModel ?? { items: [] };
    setFilterModel(sanitizeFilterModel(stored, columns));
  }, [gridItemState, withFilterPersistence, columns]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
      if (withFilterPersistence) {
        setFilterModel(model);
        saveFilterModel(model);
      }
      callerOnFilterModelChange?.(model, details);
    },
    [withFilterPersistence, saveFilterModel, callerOnFilterModelChange]
  );

  const handleVisibilityApply = useCallback(
    (model: Record<string, boolean>) => {
      const merged = {
        ...model,
        ...structuralVisibility,
        ...rowGroupingHidden,
      };
      setColumnVisibilityModel(merged);
      saveVisibility(merged);
      // Reset auto-fit so the next render recomputes column widths from scratch
      // instead of inflating/deflating the last column based on stale stored widths.
      initializedKeyRef.current = '';
      saveWidths({});
      setComputedWidths({});
      setVisibilityModalOpen(false);
    },
    [saveVisibility, saveWidths, structuralVisibility, rowGroupingHidden]
  );

  // ── Container width ─────────────────────────────────────────────────────────

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!withAutoFit) return;
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.offsetWidth); // read synchronously before first paint
    let rafId: number;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(rafId); // debounce — avoids mid-drag scrollbar flicker
      rafId = requestAnimationFrame(() => {
        const w = entries[0]?.contentRect.width;
        if (w) setContainerWidth(w);
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [withAutoFit]);

  // ── First-time initialisation ───────────────────────────────────────────────

  // Prevents re-running when deps have unstable references across renders
  // (e.g. pinnedFields is a new Set when initialState is passed as a literal).
  const initializedKeyRef = useRef('');

  // Stored column widths — set once by the auto-fit init logic and updated only
  // for the column the user actually resized. No redistribution to other columns.
  const [computedWidths, setComputedWidths] = useState<Record<string, number>>(
    () => loadGridState()[name]?.columnWidthMap ?? {}
  );

  // Ref mirror of computedWidths — lets the container-resize effect read the
  // latest widths without being listed as a dep (avoids retriggering on every resize).
  const computedWidthsRef = useRef(computedWidths);
  useEffect(() => {
    computedWidthsRef.current = computedWidths;
  }, [computedWidths]);

  // Ref mirror of pinnedFields for the same reason.
  const pinnedFieldsRef = useRef(pinnedFields);
  useEffect(() => {
    pinnedFieldsRef.current = pinnedFields;
  }, [pinnedFields]);

  // Ref mirror of columnVisibilityModel for the container-resize effect.
  const columnVisibilityModelRef = useRef(columnVisibilityModel);
  useEffect(() => {
    columnVisibilityModelRef.current = columnVisibilityModel;
  }, [columnVisibilityModel]);

  // Ref mirror of groupingColDef — used in effects where it must not be a dep.
  const groupingColDefRef = useRef(groupingColDef);
  useEffect(() => {
    groupingColDefRef.current = groupingColDef;
  }, [groupingColDef]);

  // Sync computedWidths when gridItemState changes (e.g. cross-tab storage event).
  useEffect(() => {
    setComputedWidths(gridItemState?.columnWidthMap ?? {});
  }, [gridItemState]);

  // ── Container resize — grow/shrink the last column by the delta ─────────────

  const prevContainerWidthRef = useRef(0);

  useEffect(() => {
    if (!withAutoFit || containerWidth <= 0) return;

    const prev = prevContainerWidthRef.current;
    prevContainerWidthRef.current = containerWidth;

    if (prev <= 0 || prev === containerWidth) return;

    const widths = computedWidthsRef.current;
    const visModel = columnVisibilityModelRef.current;
    const allStored = columns
      .filter(
        (c) =>
          !pinnedFieldsRef.current.has(c.field) && visModel[c.field] !== false
      )
      .every((c) => widths[c.field] != null);
    if (!allStored) return; // let init handle un-stored columns

    // When a flex grouping column is present it absorbs container size changes
    // automatically — adjusting a regular column would displace the grouping col.
    // If the user has explicitly resized the grouping col (stored width), it is
    // now fixed-width and we DO need to adjust the last regular column instead.
    if (
      groupingColDefRef.current != null &&
      computedWidthsRef.current[GROUPING_COL_FIELD] == null
    )
      return;

    const delta = containerWidth - prev;
    const lastCol = [...columns]
      .reverse()
      .find(
        (c) =>
          !pinnedFieldsRef.current.has(c.field) && visModel[c.field] !== false
      );
    if (!lastCol) return;

    const lastWidth = widths[lastCol.field] ?? 0;
    const newWidth = Math.max(lastWidth + delta, lastCol.minWidth ?? 50);
    const newWidths = { ...widths, [lastCol.field]: newWidth };
    saveWidths(newWidths);
    setComputedWidths(newWidths);
  }, [containerWidth, withAutoFit, columns, saveWidths]);

  useEffect(() => {
    if (!withAutoFit || containerWidth <= 0) return;

    // Use the authoritative merged visibility so that intermediate render states
    // (e.g. shiftName not yet hidden by columnVisibilityModel state during a
    // groupBy transition) don't cause wrong initial widths to be stored.
    const effectiveVis = {
      ...columnVisibilityModel,
      ...structuralVisibility,
      ...rowGroupingHidden,
    };

    // Key encodes visible non-pinned fields so the effect re-runs whenever
    // visibility changes — hidden columns must not affect width calculations.
    const visibleNonPinnedCols = columns.filter(
      (c) => !pinnedFields.has(c.field) && effectiveVis[c.field] !== false
    );
    const key = visibleNonPinnedCols.map((c) => c.field).join(',');
    if (initializedKeyRef.current === key) return;

    // Also require the grouping column to be stored — it is part of the layout
    // and must be initialised together with regular columns.
    const groupingAlsoStored =
      groupingColDef == null ||
      gridItemState?.columnWidthMap[GROUPING_COL_FIELD] != null;
    const allStored =
      groupingAlsoStored &&
      visibleNonPinnedCols.every(
        (c) => gridItemState?.columnWidthMap[c.field] != null
      );

    if (allStored) {
      initializedKeyRef.current = key;

      // All widths are stored but there may be leftover space (e.g. columns were
      // hidden or the user previously used a narrower screen).
      // Add the remainder to the last visible column.
      const storedMap = gridItemState!.columnWidthMap;
      const visibleCols = columns.filter(
        (c) => effectiveVis[c.field] !== false
      );
      const knownPinnedTotal = visibleCols
        .filter((c) => pinnedFields.has(c.field))
        .reduce((sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH), 0);
      const nonPinnedTotal = visibleNonPinnedCols.reduce(
        (sum, c) => sum + (storedMap[c.field] ?? 0),
        0
      );
      const available = containerWidth - knownPinnedTotal - SCROLL_GUARD_PX;
      const remainder = available - nonPinnedTotal;

      // When a flex grouping column is present it fills the leftover space
      // automatically — inflating the last regular column would shrink the
      // grouping column instead of filling an actual gap.
      if (remainder > 0 && groupingColDef == null) {
        const lastCol = [...visibleNonPinnedCols].reverse().find(() => true);
        if (lastCol) {
          const adjusted = {
            ...storedMap,
            [lastCol.field]: (storedMap[lastCol.field] ?? 0) + remainder,
          };
          saveWidths(adjusted);
          setComputedWidths(adjusted);
        }
      }
      return;
    }

    const visibleCols = columns.filter((c) => effectiveVis[c.field] !== false);
    if (columnsExceedContainer(visibleCols, containerWidth)) {
      initializedKeyRef.current = key;
      return;
    }

    initializedKeyRef.current = key;

    let reservedForGrouping = 0;
    if (groupingColDef != null) {
      const knownPinnedTotal = visibleCols
        .filter((c) => pinnedFields.has(c.field))
        .reduce((sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH), 0);
      const rawAvailable = containerWidth - knownPinnedTotal - SCROLL_GUARD_PX;
      reservedForGrouping = computeGroupingColInitialWidth(
        groupingColDef,
        visibleNonPinnedCols,
        rawAvailable
      );
    }

    const initWidths = computeInitialWidths(
      visibleCols,
      containerWidth,
      pinnedFields,
      reservedForGrouping
    );
    if (reservedForGrouping > 0) {
      initWidths[GROUPING_COL_FIELD] = reservedForGrouping;
    }
    saveWidths(initWidths);
    setComputedWidths(initWidths);
  }, [
    gridItemState,
    containerWidth,
    columns,
    pinnedFields,
    columnVisibilityModel,
    structuralVisibility,
    rowGroupingHidden,
    withAutoFit,
    saveWidths,
    groupingColDef,
  ]);

  // ── Column resize ───────────────────────────────────────────────────────────

  const onColumnWidthChange: GridEventListener<'columnWidthChange'> =
    useCallback(
      (params) => {
        const resizedField = params.colDef.field;

        // Hidden columns are excluded — they take no space in the grid.
        const nonPinnedCols = columns.filter(
          (c) =>
            !pinnedFields.has(c.field) &&
            columnVisibilityModel[c.field] !== false
        );

        // Seed newWidths from all non-pinned columns so every column's width is
        // persisted on any resize — even for grids where columnsExceedContainer
        // is true and computedWidths was never populated by auto-fit init.
        const newWidths: Record<string, number> = {};
        for (const col of nonPinnedCols) {
          newWidths[col.field] = Math.max(
            computedWidths[col.field] ??
              col.width ??
              PINNED_COLUMN_DEFAULT_WIDTH,
            col.minWidth ?? 0
          );
        }

        // The grouping column is MUI-generated (not in `columns` prop). Persist its
        // width explicitly so it survives re-renders, and transfer the delta to/from
        // the last regular column to keep the total equal to containerWidth.
        if (resizedField === GROUPING_COL_FIELD) {
          // Use the stored previous width when available; fall back to deriving it
          // from the container budget so the first resize also works.
          const knownPinnedTotal = columns
            .filter((c) => pinnedFields.has(c.field))
            .reduce(
              (sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH),
              0
            );
          const regularTotal = nonPinnedCols.reduce(
            (sum, c) => sum + (newWidths[c.field] ?? 0),
            0
          );
          const prevGroupingWidth =
            computedWidths[GROUPING_COL_FIELD] ??
            containerWidth - knownPinnedTotal - regularTotal;
          const delta = params.width - prevGroupingWidth;
          const lastCol = [...nonPinnedCols].reverse().find(() => true);
          if (lastCol) {
            newWidths[lastCol.field] = Math.max(
              (newWidths[lastCol.field] ?? 0) - delta,
              lastCol.minWidth ?? 50
            );
          }
          // Persist the grouping column's explicit width so it doesn't reset on re-render.
          newWidths[GROUPING_COL_FIELD] = params.width;
          saveWidths(newWidths);
          setComputedWidths(newWidths);
          return;
        }

        newWidths[resizedField] = params.width;

        const lastNonPinnedField =
          nonPinnedCols[nonPinnedCols.length - 1]?.field;
        const isLastColShrunk =
          resizedField === lastNonPinnedField &&
          params.width < (computedWidths[resizedField] ?? params.width);
        const knownPinnedTotal = columns
          .filter((c) => pinnedFields.has(c.field))
          .reduce(
            (sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH),
            0
          );

        const storedGroupingWidth = computedWidths[GROUPING_COL_FIELD] ?? null;

        if (
          isLastColShrunk &&
          (storedGroupingWidth != null || groupingColDef == null)
        ) {
          // Snap the last column to fill all available space so no whitespace
          // appears on the right. Subtract the fixed grouping col width when
          // present; when grouping col is flex it is not included (offset = 0).
          const fixedGroupingOffset = storedGroupingWidth ?? 0;
          const otherNonPinnedTotal = nonPinnedCols
            .filter((c) => c.field !== lastNonPinnedField)
            .reduce((sum, c) => sum + (computedWidths[c.field] ?? 0), 0);
          const fillWidth = Math.max(
            containerWidth -
              knownPinnedTotal -
              fixedGroupingOffset -
              otherNonPinnedTotal -
              SCROLL_GUARD_PX,
            nonPinnedCols[nonPinnedCols.length - 1]?.minWidth ?? 50
          );
          if (params.width <= fillWidth) {
            newWidths[lastNonPinnedField] = fillWidth;
          }
        } else if (!isLastColShrunk) {
          // Middle column was shrunk: redistribute the freed space to the last
          // column so no gap appears on the right. This runs regardless of whether
          // a grouping column is present — with a flex grouping col the budget
          // stays equal so the grouping col width is preserved too.
          const budget = nonPinnedCols.reduce(
            (sum, c) =>
              sum +
              Math.max(
                computedWidths[c.field] ??
                  c.width ??
                  PINNED_COLUMN_DEFAULT_WIDTH,
                c.minWidth ?? 0
              ),
            0
          );
          const newNonPinnedTotal = nonPinnedCols.reduce(
            (sum, c) => sum + (newWidths[c.field] ?? 0),
            0
          );
          if (newNonPinnedTotal < budget) {
            const receivingCol = [...nonPinnedCols]
              .reverse()
              .find((c) => c.field !== resizedField);
            if (receivingCol) {
              const gap = budget - newNonPinnedTotal;
              newWidths[receivingCol.field] = Math.max(
                (newWidths[receivingCol.field] ?? 0) + gap,
                receivingCol.minWidth ?? 50
              );
            }
          }
        }

        // Carry the grouping column's stored width so a regular-column resize
        // doesn't erase it (saveWidths replaces the entire columnWidthMap).
        const existingGroupingWidth = computedWidths[GROUPING_COL_FIELD];
        if (existingGroupingWidth != null) {
          newWidths[GROUPING_COL_FIELD] = existingGroupingWidth;
        }

        saveWidths(newWidths);
        setComputedWidths(newWidths);
      },
      [
        saveWidths,
        computedWidths,
        columns,
        pinnedFields,
        containerWidth,
        columnVisibilityModel,
        groupingColDef,
      ]
    );

  // When the user has explicitly resized the grouping column, persist its width
  // as a hard prop so MUI doesn't revert it to flex-computed on re-render.
  const effectiveGroupingColDef = useMemo(() => {
    if (!groupingColDef) return groupingColDef;
    const storedWidth = computedWidths[GROUPING_COL_FIELD];
    if (storedWidth == null) return groupingColDef;
    const { flex: _, ...rest } = groupingColDef as any;
    return { ...rest, width: storedWidth };
  }, [groupingColDef, computedWidths]);

  const { columnsWithConfig, hideHorizontalScrollbar, lastVisibleField } =
    useMemo(() => {
      // Merge structural + rowGrouping visibility synchronously so transitions
      // between grouping models never let a grouped column slip through as visible
      // while the state effect hasn't fired yet.
      const effectiveVisibility = {
        ...columnVisibilityModel,
        ...structuralVisibility,
        ...rowGroupingHidden,
      };

      // Only visible columns participate in width calculations.
      // Hidden columns take no space so they must be excluded from all sizing logic.
      // `configured` (passed to DataGridPremium) still maps ALL columns so MUI can
      // read their definitions; the `columnVisibilityModel` prop controls hiding.
      const visibleCols = columns.filter(
        (c) => effectiveVisibility[c.field] !== false
      );
      const visibleNonPinnedCols = visibleCols.filter(
        (c) => !pinnedFields.has(c.field)
      );

      const allStored =
        (groupingColDef == null ||
          computedWidths[GROUPING_COL_FIELD] != null) &&
        visibleNonPinnedCols.every((c) => computedWidths[c.field] != null);

      // First visit with a known container: distribute space evenly inline so the
      // grid fills immediately without waiting for the useEffect to persist widths.
      const needsInit =
        withAutoFit &&
        containerWidth > 0 &&
        !allStored &&
        !columnsExceedContainer(visibleCols, containerWidth);

      let widths = computedWidths;
      if (needsInit) {
        // Compute the grouping column's proportional share first so it gets a
        // reasonable initial width and regular columns don't crowd it out.
        let reservedForGrouping = 0;
        if (groupingColDef != null) {
          const knownPinnedTotal = visibleCols
            .filter((c) => pinnedFields.has(c.field))
            .reduce(
              (sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH),
              0
            );
          const rawAvailable =
            containerWidth - knownPinnedTotal - SCROLL_GUARD_PX;
          reservedForGrouping = computeGroupingColInitialWidth(
            groupingColDef,
            visibleNonPinnedCols,
            rawAvailable
          );
        }
        widths = computeInitialWidths(
          visibleCols,
          containerWidth,
          pinnedFields,
          reservedForGrouping
        );
        if (reservedForGrouping > 0) {
          widths = { ...widths, [GROUPING_COL_FIELD]: reservedForGrouping };
        }
      }

      // Apply container resize delta synchronously so the grid renders correctly
      // on the first pass — the useEffect below persists the same change afterward.
      // prevContainerWidthRef holds the containerWidth from the previous render's
      // useEffect, so reading it here gives us the correct "old" value.
      if (!needsInit && allStored && withAutoFit) {
        const prev = prevContainerWidthRef.current;
        if (prev > 0 && prev !== containerWidth) {
          // A flex grouping column absorbs container changes automatically;
          // only adjust the last regular column when the grouping col is fixed
          // (user has resized it and its width is stored).
          const hasFlexGroupingCol =
            groupingColDef != null && widths[GROUPING_COL_FIELD] == null;
          if (!hasFlexGroupingCol) {
            const delta = containerWidth - prev;
            const lastCol = [...visibleNonPinnedCols]
              .reverse()
              .find(() => true);
            if (lastCol) {
              const lastWidth = widths[lastCol.field] ?? 0;
              const newWidth = Math.max(
                lastWidth + delta,
                lastCol.minWidth ?? 50
              );
              widths = { ...widths, [lastCol.field]: newWidth };
            }
          }
        } else if (containerWidth > 0) {
          // Stored widths exist but their sum doesn't fill the container (e.g. the
          // user returns to a wider viewport, or columns were hidden freeing space).
          // Add the gap to the last visible non-pinned column synchronously.
          const knownPinnedTotal = visibleCols
            .filter((c) => pinnedFields.has(c.field))
            .reduce(
              (sum, c) => sum + (c.width ?? PINNED_COLUMN_DEFAULT_WIDTH),
              0
            );
          const nonPinnedTotal = visibleNonPinnedCols.reduce(
            (sum, c) => sum + (widths[c.field] ?? 0),
            0
          );
          const available = containerWidth - knownPinnedTotal - SCROLL_GUARD_PX;
          const remainder = available - nonPinnedTotal;
          // When a flex grouping column is present it already fills the remainder —
          // inflating the last regular column would shrink the grouping column.
          if (remainder > 0 && groupingColDef == null) {
            const lastCol = [...visibleNonPinnedCols]
              .reverse()
              .find(() => true);
            if (lastCol) {
              widths = {
                ...widths,
                [lastCol.field]: (widths[lastCol.field] ?? 0) + remainder,
              };
            }
          }
        }
      }

      // Map ALL columns (including hidden) so DataGridPremium knows their defs.
      // Width assignments for hidden columns are harmless — MUI ignores them.
      const configured = columns
        .map(applyBounds(applyDefaultBounds))
        .map((col): GridColDef => {
          // Pinned columns always use their declared width (or the default).
          // They are excluded from auto-fit and redistribution entirely.
          if (pinnedFields.has(col.field)) {
            const { flex: _, ...base } = col;
            return { ...base, width: col.width ?? PINNED_COLUMN_DEFAULT_WIDTH };
          }

          const stored = widths[col.field];
          if (stored != null) {
            const { flex: _, ...base } = col;
            return {
              ...base,
              width: Math.round(Math.max(stored, col.minWidth ?? 0)),
            };
          }
          if (!col.flex && !col.width) return { ...col, flex: 1 };
          return col;
        });
      const totalWidth = configured.reduce((sum, c) => sum + (c.width ?? 0), 0);

      // Inject the column visibility toggle icon into the last *visible* column's
      // header. When the last column is hidden the icon moves to the new last
      // visible column so it is always reachable.
      let lastVisibleIndex = -1;
      for (let i = configured.length - 1; i >= 0; i--) {
        if (effectiveVisibility[configured[i].field] !== false) {
          lastVisibleIndex = i;
          break;
        }
      }
      const configuredWithIcon = configured.map((col, index) => {
        if (!withColumnVisibilityIcon || index !== lastVisibleIndex) return col;
        const originalRenderHeader = col.renderHeader;
        return {
          ...col,
          renderHeader: (params: GridColumnHeaderParams) => {
            const headerAlign = params.colDef?.headerAlign ?? 'left';
            const labelJustify =
              headerAlign === 'center'
                ? 'center'
                : headerAlign === 'right'
                ? 'flex-end'
                : 'flex-start';
            return (
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                minWidth={0}
                gap={0.5}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={labelJustify}
                  flex={1}
                  minWidth={0}
                  overflow="hidden"
                >
                  <span
                    className="MuiDataGrid-columnHeaderTitle"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {originalRenderHeader
                      ? originalRenderHeader(params)
                      : params.colDef.headerName ?? ''}
                  </span>
                </Box>
                <IconButton
                  size="small"
                  title="Manage columns"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVisibilityModalOpen(true);
                  }}
                  sx={{ flexShrink: 0, p: 0.25 }}
                >
                  <Icon name="SettingsRounded" sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            );
          },
        };
      });

      return {
        columnsWithConfig: configuredWithIcon,
        hideHorizontalScrollbar:
          containerWidth > 0 && totalWidth <= containerWidth,
        lastVisibleField:
          lastVisibleIndex >= 0 ? configured[lastVisibleIndex].field : null,
      };
    }, [
      columns,
      applyDefaultBounds,
      containerWidth,
      computedWidths,
      withAutoFit,
      withColumnVisibilityIcon,
      pinnedFields,
      columnVisibilityModel,
      structuralVisibility,
      rowGroupingHidden,
      groupingColDef,
    ]);

  const slots = useMemo(
    () => ({
      ...props?.slots,
      detailPanelExpandIcon: (p: any) => (
        <Icon name="ExpandCircleDown" {...p} sx={{ color: 'primary.main' }} />
      ),
      detailPanelCollapseIcon: (p: any) => (
        <Icon
          name="ExpandCircleDown"
          {...p}
          sx={{ transform: 'rotate(180deg)', color: 'primary.main' }}
        />
      ),
    }),
    [props?.slots]
  );

  const slotProps = useMemo(
    () => ({
      ...props?.slotProps,
      panel: {
        ...props?.slotProps?.panel,
        placement: 'bottom-start',
        sx: {
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 3,
          borderRadius: 0.5,
          ...(props?.slotProps?.panel as any)?.sx,
        },
        popperOptions: {
          modifiers: [{ name: 'offset', options: { offset: [4, 4] } }],
        },
      },
    }),
    [props?.slotProps]
  );

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <DataGridPremium
        disableColumnSelector
        disablePivoting
        {...props}
        groupingColDef={effectiveGroupingColDef}
        initialState={initialState}
        pinnedColumns={pinnedColumns}
        columns={columnsWithConfig}
        columnVisibilityModel={{
          ...columnVisibilityModel,
          ...structuralVisibility,
          ...rowGroupingHidden,
        }}
        {...(withFilterPersistence && {
          filterModel,
          onFilterModelChange: handleFilterModelChange,
        })}
        {...(!withFilterPersistence &&
          callerOnFilterModelChange && {
            onFilterModelChange: callerOnFilterModelChange,
          })}
        onColumnWidthChange={onColumnWidthChange}
        slots={slots}
        slotProps={slotProps}
        getRowId={getRowId}
        pinnedColumnsSectionSeparator="border"
        sx={(theme) => ({
          ...(lastVisibleField && {
            [`& [data-field="${lastVisibleField}"] .MuiDataGrid-columnHeaderTitleContainerContent`]:
              { flex: 1, minWidth: 0, overflow: 'hidden' },
            [`& [data-field="${lastVisibleField}"] .MuiDataGrid-aggregationColumnHeader`]:
              { width: '100%' },
          }),
          ...(hideHorizontalScrollbar && {
            '& .MuiDataGrid-scrollbar--horizontal': { display: 'none' },
          }),
          ...(typeof props.sx === 'function'
            ? props.sx(theme)
            : (props.sx as any) ?? {}),
        })}
      />
      <ColumnVisibilityModal
        open={visibilityModalOpen}
        columns={columns as GridColDef[]}
        pinnedFields={pinnedFields}
        visibilityModel={columnVisibilityModel}
        onClose={() => setVisibilityModalOpen(false)}
        onApply={handleVisibilityApply}
      />
    </div>
  );
};
